import { ProductData } from '@/common/types/product.type'
import { ProductCategoryData } from '@/common/types/productCategory.type'
import { unFormatCurrency, unFormatUnknownUnit } from '@/common/utils/unFormatters'
import { CompanyContractConsumptionControlledBy } from '@/features/company/types/contract.type'
import { z } from 'zod'

export const entryProductCurrency = z.object({
  id: z.string().optional().nullable(),
  product: z.custom<ProductData>(),
  total_value: z.string().refine((value) => !!value && unFormatCurrency(value) > 0, { message: 'Campo obrigatório' }),
})

export const entryProductQuantity = z.object({
  id: z.string().optional().nullable(),
  product: z.custom<ProductData>(),
  total_quantity: z
    .string()
    .refine((value) => !!value && unFormatUnknownUnit(value) > 0, { message: 'Campo obrigatório' }),
  total_value: z.string().nullable().optional(),
})

export const entryProductCategoryCurrencySchema = z.object({
  id: z.string().optional().nullable(),
  product_category: z.custom<ProductCategoryData>(),
  total_value: z.string().refine((value) => !!value && unFormatCurrency(value) > 0, { message: 'Campo obrigatório' }),
  entry_products: z.array(entryProductCurrency),
})

export const entryProductCategoryQuantitySchema = z.object({
  id: z.string().optional().nullable(),
  product_category: z.custom<ProductCategoryData>(),
  total_quantity: z
    .string()
    .refine((value) => !!value && unFormatUnknownUnit(value) > 0, { message: 'Campo obrigatório' }),
  total_value: z.string().nullable().optional(),
  entry_products: z.array(entryProductQuantity),
  governmentAllocationCurrencyRestricted: z.boolean().nullable().optional(),
}).superRefine((data, ctx) => {
  if (data.governmentAllocationCurrencyRestricted) {
    if (!data.total_value || unFormatCurrency(data.total_value) === 0) {
      ctx.addIssue({
        path: ['total_value'],
        code: z.ZodIssueCode.custom,
        message: 'Campo obrigatório',
      });
    }

    data.entry_products.forEach((entryProduct, index) => {
      if (!entryProduct.total_value || unFormatCurrency(entryProduct.total_value) === 0) {
        ctx.addIssue({
          path: [`entry_products.${index}.total_value`],
          code: z.ZodIssueCode.custom,
          message: 'Campo obrigatório',
        });
      }
    });
  }
})

export const entryProduct = entryProductCurrency
export const entryProductCategorySchema = entryProductCategoryCurrencySchema

const numberForIntegrationSchema = z
  .string()
  .optional()
  .nullable()
  .transform((val) => (val === '' ? null : val))
  .refine(
    (value) => {
      if (!value) return true
      return /^\d{1,6}\/\d{4}$/.test(value)
    },
    { message: 'Use o formato XXXXXX/AAAA (ex: 123456/2026)' },
  )

export const updateCurrencySchema = z.object({
  office_number: z.string().optional().nullable(),
  number_for_integration: numberForIntegrationSchema,
  government_allocation: z
    .object({
      start_of_document_validity: z
        .string()
        .optional()
        .nullable()
        .refine(
          (value) => {
            if (!!value && value.length < 10) return false
            return true
          },
          { message: 'Use uma data válida' },
        ),
      end_of_document_validity: z
        .string()
        .optional()
        .nullable()
        .refine(
          (value) => {
            if (!!value && value.length < 10) return false
            return true
          },
          { message: 'Use uma data válida' },
        ),
    })
    .optional()
    .nullable(),
  total_value: z.string().optional().nullable(),
  entry_product_categories: z.array(entryProductCategoryCurrencySchema).optional(),
})

export const updateQuantitySchema = z.object({
  office_number: z.string().optional().nullable(),
  number_for_integration: numberForIntegrationSchema,
  government_allocation: z
    .object({
      start_of_document_validity: z
        .string()
        .optional()
        .nullable()
        .refine(
          (value) => {
            if (!!value && value.length < 10) return false
            return true
          },
          { message: 'Use uma data válida' },
        ),
      end_of_document_validity: z
        .string()
        .optional()
        .nullable()
        .refine(
          (value) => {
            if (!!value && value.length < 10) return false
            return true
          },
          { message: 'Use uma data válida' },
        ),
    })
    .optional()
    .nullable(),
  total_quantity: z.string().optional().nullable(),
  entry_product_categories: z.array(entryProductCategoryQuantitySchema),
})

export const updateSchema = updateCurrencySchema

export const getUpdateSchema = (controlledBy: CompanyContractConsumptionControlledBy) => {
  return controlledBy === 'quantity' ? updateQuantitySchema : updateCurrencySchema
}

export type GovernmentAllocationEntryUpdateValidationSchema = z.infer<typeof updateSchema>
export type GovernmentAllocationEntryProductValidationSchema = z.infer<typeof entryProduct>
export type GovernmentAllocationEntryProductCategoryValidationSchema = z.infer<typeof entryProductCategorySchema>
export type GovernmentAllocationEntryProductCategoryCurrencyValidationSchema = z.infer<
  typeof entryProductCategoryCurrencySchema
>
export type GovernmentAllocationEntryProductCategoryQuantityValidationSchema = z.infer<
  typeof entryProductCategoryQuantitySchema
>
